import type { ConsentStrategy } from './ConsentStrategy';

declare global {
  interface Window {
    /**
     * Typed `unknown` on purpose: this is a third-party global whose shape we cannot trust,
     * so every read is narrowed at runtime instead of asserted at compile time.
     */
    Cookiebot?: unknown;
  }
}

/**
 * Reads the canonical `Cookiebot` global only. The `window.CookieConsent` alias is
 * collision-prone — other consent tools publish a global by that name — so probing it would
 * make us claim a Cookiebot shop that isn't one.
 */
function readCookiebotConsent(): Record<string, unknown> | null {
  const cookiebot = window.Cookiebot;
  if (typeof cookiebot !== 'object' || cookiebot === null) {
    return null;
  }

  const consent = (cookiebot as { consent?: unknown }).consent;
  if (typeof consent !== 'object' || consent === null) {
    return null;
  }

  return consent as Record<string, unknown>;
}

/**
 * BirdsView is behavioural analytics, so shops declare it under Cookiebot's Statistics
 * category. Before the visitor answers the banner Cookiebot already reports
 * `statistics: false`, which is the correct closed state — no extra "not answered yet" case.
 */
export class CookiebotStrategy implements ConsentStrategy {
  readonly name = 'cookiebot';

  isPresent(): boolean {
    return readCookiebotConsent() !== null;
  }

  hasConsent(): boolean {
    return readCookiebotConsent()?.statistics === true;
  }
}
